import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { connect } from 'react-redux';

import { VpMediaTagInput } from '../../';
import VpConditionalUpgradeWrapper from '../../components/VpConditionalUpgradeWrapper/VpConditionalUpgradeWrapper';
import {
  getContentTaggingEnabled,
  getMediaThumbnailEditingEnabled
} from '../../../redux/modules/auth';
import { VpVideoPlayer, VpIcon, VpDocViewer, VpButton } from '../../components';
import { useMediaTableContext, useForceDownload } from '../../../hooks';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import { useVpSnackbar } from '../../hooks/index';

const VideoPlayerContainer = styled(Box)({
  justifyContent: 'center',
  alignContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '6px',
  height: '60vh',
  width: '100%'
});

const StyledBlurredImage = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  filter: 'blur(20px) brightness(.75) drop-shadow(0 0 20px black);'
});
const StyledIcon = styled(VpIcon)({
  position: 'relative',
  width: '100%',
  height: '100%'
});
const StyledImage = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});
const StyledSkeleton = styled(Skeleton)({
  width: '100%',
  height: '100%',
  position: 'absolute'
});

export const VpMediaInfoPreview = ({ media, contentTaggingEnabled, customizeThumbnailEnabled }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const isVideo = media?.type === 'VIDEO';
  const isImage = media?.type === 'IMAGE';
  const isDocument = !isVideo && !isImage;
  const thumbnailUrl = media?.thumbnailUrl || media?.path;

  const { setUpdatingThumbnail } = useMediaTableContext();
  const { addSnackbarInfo, addSnackbarError } = useVpSnackbar();

  const handleCustomizeThumbnail = () => {
    if (customizeThumbnailEnabled) {
      return setUpdatingThumbnail(media?.mediaId);
    }

    addSnackbarError({
      message:
        'Your current plan does not allow customizing video thumbnails. Please upgrade on the Billing page.',
      title: 'Error!'
    });
  };

  const { forceDownload } = useForceDownload();

  const handleOnLoad = () => {
    setIsLoading(false);
  };
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const buttons = [
    {
      label: 'Customize Thumbnail',
      icon: 'Image',
      action: handleCustomizeThumbnail,
      visible: !!isVideo
    },
    {
      label: 'Download',
      icon: 'Download',
      action: () => {
        if (!isVideo) {
          forceDownload(media?.previewUrl, media?.originalFileName);
          return;
        }
        const mediaUrlSplit = media?.mediaUrl?.split('/');
        const fileName = mediaUrlSplit[mediaUrlSplit.length - 1];
        forceDownload(media?.mediaUrl, fileName);
        return;
      },
      visible: true
    },
    {
      label: 'Share',
      icon: 'Share',
      action: () => {
        copyToClipboard(`${window?.location?.origin}/share/${media?.mediaRawPath}`);
        addSnackbarInfo({ message: 'Copied link to clipboard', title: '' });
      },
      visible: !!isVideo
    }
  ];

  return (
    <Stack sx={{ alignItems: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <VpConditionalUpgradeWrapper condition={!contentTaggingEnabled} disableFlex>
          {({ isDisabled }) => (
            <VpMediaTagInput
              value={media?.tags}
              mediaId={media?.mediaId || media?.attachmentId}
              isVideo={!!media?.mediaId}
              disabled={isDisabled}
            />
          )}
        </VpConditionalUpgradeWrapper>
      </Box>
      <div style={{ display: 'flex', width: '100%' }}>
        {buttons.map(
          (b) =>
            b.visible && (
              <VpButton
                key={b.label}
                onClick={b.action}
                variant="outlined"
                color="neutral"
                sx={{ gap: '10px', flexGrow: 1, flexBasis: '33%' }}
              >
                <VpIcon icon={b.icon} sx={{ width: '16px' }} /> {b.label}
              </VpButton>
            )
        )}
      </div>
      <VideoPlayerContainer>
        {isDocument && <VpDocViewer document={media} />}
        {!hasError && <StyledBlurredImage component="img" src={thumbnailUrl} />}
        {isVideo ? (
          <VpVideoPlayer
            onLoad={handleOnLoad}
            mediaUrl={media?.mediaUrl}
            thumbnailUrl={thumbnailUrl}
            noDownload
          />
        ) : hasError ? (
          <StyledIcon icon="Paperclip" />
        ) : (
          <StyledImage
            key={thumbnailUrl}
            component="img"
            onLoad={handleOnLoad}
            onError={handleError}
            src={thumbnailUrl}
          />
        )}
        {isLoading && <StyledSkeleton variant="rectangular" />}
      </VideoPlayerContainer>
    </Stack>
  );
};

VpMediaInfoPreview.propTypes = {
  media: PropTypes.shape({
    mediaUrl: PropTypes.string,
    mediaId: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    path: PropTypes.string,
    attachmentId: PropTypes.string,
    type: PropTypes.string,
    tags: PropTypes.array,
    mediaRawPath: PropTypes.string,
    previewUrl: PropTypes.string,
    originalFileName: PropTypes.string
  }),
  contentTaggingEnabled: PropTypes.bool,
  customizeThumbnailEnabled: PropTypes.bool
};

const mapStateToProps = (state) => ({
  contentTaggingEnabled: getContentTaggingEnabled(state),
  customizeThumbnailEnabled: getMediaThumbnailEditingEnabled(state)
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(VpMediaInfoPreview);



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoPreview.jsx